import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  BOMItemModel,
  defaultNPD,
  NPDModel,
  NPDStatus,
} from "../../models/npd_model";
import { fetchGet, fetchPost, fetchPut } from "../../../../service/api_client";
import { urls } from "../../../../utils/urls";
import ProjectSearchFieldV2 from "../../../purchase_order/purchase_order_create/components/project_search_widget";
import BOMItemComponent from "./bom_item_component_widget";
import ModalDialog from "../../../../ui/new_components/common/modal";
import BOMReturnedItemComponent from "./returned_item_widget";
import ProductSearchField from "./productSeachField";
import { mdiChevronLeft } from "@mdi/js";
import Icon from "@mdi/react";
import Papa from "papaparse";
import PartySearchField from "../../../accounts/parties/party_search_field";
import "../../../../styles/responsive_shrink.css";

interface NPDProps {}

const NPDFinalViewScreen: FunctionComponent<NPDProps> = () => {
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd`;
  const [, setLoading] = useState(false);
  const [addNewId1, setAddNewId1] = useState(Math.random() * 1000);
  const { showToast } = useToast();
  const [checked, setChecked] = useState(false);
  const [showFinalReviewModel, setShowFinalReviewModel] = useState(false);
  const [data, setData] = useState<NPDModel>({
    ...defaultNPD,
  });
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);
  const [screen, setScreen] = useState("npd_details");

  //   const existingItems = data?.items || [];
  const navigate = useHistory();
  const existingItems = data?.bom_items || [];
  const existingReturnedItems = data?.returned_items || [];

  useEffect(() => {
    if (id) getVoucher(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getVoucher = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + "/" + id);
      if (res.success) {
        setData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to fetch voucher" });
    }
    setLoading(false);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger file input
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ["Warehouse", "Item Code", "Qty"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "return_stock_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReturnedStockCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validMimeTypes = ["text/csv", "application/vnd.ms-excel"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!validMimeTypes.includes(file.type) && fileExtension !== "csv") {
      showToast({ type: ToastType.error, text: "Only CSV files are allowed." });
      event.target.value = "";
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedRows = results.data as Array<Record<string, string>>;

        if (!parsedRows || parsedRows.length === 0) {
          showToast({
            type: ToastType.error,
            text: "CSV is empty or invalid.",
          });
          event.target.value = "";
          return;
        }

        const requiredHeaders = ["Warehouse", "Item Code", "Qty"];
        const missingHeaders = requiredHeaders.filter(
          (h) => !(h in parsedRows[0] || {}),
        );

        if (missingHeaders.length > 0) {
          showToast({
            type: ToastType.error,
            text: `Missing columns: ${missingHeaders.join(", ")}`,
          });
          event.target.value = "";
          return;
        }

        const formattedData = parsedRows.map((row) => ({
          warehouse_name: row["Warehouse"]?.trim(),
          item_code: row["Item Code"]?.trim(),
          quantity: parseInt(row["Qty"] || "0", 10),
        }));

        try {
          setLoading(true);

          const uploadUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/import-returned-stocks`;

          const response = await fetchPost(uploadUrl + "/" + data.id, {
            formattedData,
          });

          if (response.success) {
            setData((o: NPDModel) => ({
              ...o,
              returned_items: [...response.matched],
            }));
            showToast({
              type: ToastType.success,
              text: response.message || "Upload successful!",
            });
          } else {
            showToast({
              type: ToastType.error,
              text: response.error || "Upload failed.",
            });
          }
        } catch (error: any) {
          showToast({
            type: ToastType.error,
            text: "Upload failed: " + error.message,
          });
        } finally {
          setLoading(false);
          event.target.value = "";
        }
      },
      error: (error) => {
        showToast({
          type: ToastType.error,
          text: "CSV parse error: " + error.message,
        });
        event.target.value = "";
      },
    });
  };

  const submit = async (status: string) => {
    if (!status || !checked) {
      setData({
        ...data,
        status: NPDStatus.final_review_pending,
      });
      return showToast({
        type: ToastType.error,
        text: "Please check list the bom!!",
      });
    }
    setLoading(true);
    data.status = NPDStatus.submitted;
    const res = id
      ? await fetchPut(url + "/" + id, data)
      : await fetchPost(url, data);
    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.npd);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const ReturnedSubmit = async () => {
    setLoading(true);
    const res = id
      ? await fetchPut(url + "/" + id, data)
      : await fetchPost(url, data);
    if (res.success) {
      showToast({ type: ToastType.success, text: res.message });
      navigate.push(urls.npd);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Items</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}
      {showFinalReviewModel && (
        <ModalDialog
          show={showFinalReviewModel}
          title={""}
          onClose={() => {
            setShowFinalReviewModel(false);
            setData({
              ...data,
              status: NPDStatus.submitted,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-md">
            <h2 className="text-md font-semibold mb-4">
              Are you sure to submit the BOM? Please make sure, once submitted,
              you will not be able to edit the BOM.
            </h2>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  setShowFinalReviewModel(false);
                  setData({
                    ...data,
                    status: NPDStatus.final_review_pending,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={() => {
                  submit(NPDStatus.submitted);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      <div className="mobile-shrink sticky top-0 mt-2 px-2 md:px-6 h-full">
        <div className="flex items-center mb-2">
          <button
            className="  flex  items-center px-2 hover:text-gray-700  font-bold text-lg"
            onClick={() => {
              navigate.goBack();
            }}
          >
            <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
            Back
          </button>
          <div className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
            NPD
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-10 mb-2">
          <button
            className={`text-lg font-bold pl-2 ${
              screen === "npd_details"
                ? "bg-myPrimaryColor text-white rounded-md p-1"
                : "text-myPrimaryColor"
            }`}
            onClick={() => {
              if (id) {
                setScreen("npd_details");
                navigate.push(`${urls.npd}/view/${id}`);
              }
            }}
          >
            NPD Details
          </button>
          {data.status === NPDStatus.submitted && (
            <button
              className={`text-lg font-bold pl-2 ${
                screen === "inward"
                  ? "bg-myPrimaryColor text-white rounded-md p-1"
                  : "text-myPrimaryColor"
              }`}
              onClick={() => {
                if (id) {
                  setScreen("inward");
                  navigate.push(`${urls.npd}/inward/${id}`);
                }
              }}
            >
              Inward
            </button>
          )}
        </div>
        <div className="bg-white rounded p-4 md:p-8 my-4 md:my-10 flex gap-1 h-auto overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 p-2 md:p-5 text-sm w-full lg:w-3/4 font-semibold">
            <div className="">
              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold">Project *</label>
                <ProjectSearchFieldV2
                  disabled={true} // Always editable
                  placeholder="Select Project"
                  value={data.project}
                  onSelect={(d) => {
                    setData((prevData) => ({
                      ...prevData,
                      project: {
                        ...prevData.project!,
                        name: d.name,
                        description: d.description,
                      },
                    }));
                  }}
                />
              </div>
              <div className="my-2">
                <label htmlFor="" className="text-sm font-semibold">
                  Product Name *
                </label>{" "}
                <ProductSearchField
                  disabled={true}
                  value={data?.product}
                  onSelect={(d) => {
                    if (d.type === "Product") {
                      setData((o) => ({
                        ...o,
                        product: d,
                        product_id: d.id,
                      }));
                    }
                  }}
                />
              </div>
            </div>
            <div className="">
              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold"> Type *</label>
                <input
                  type="text"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={data.type}
                  disabled={true}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-md pt-1 font-semibold"> Qty</label>
                <input
                  type="text"
                  required
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={data.qty}
                  onChange={(e) => {
                    setData((old) => {
                      return {
                        ...old,
                        qty: Number(e.target.value),
                      };
                    });
                  }}
                  disabled={true}
                />
              </div>
              <div className="mt-4">
                {data.type && data?.type === "External" && (
                  <div className="flex flex-col gap-1 w-full">
                    <label className="text-md font-semibold">Vendor *</label>
                    <PartySearchField
                      disabled={true}
                      placeholder="Vendor"
                      value={data.party}
                      onSelect={(d) =>
                        setData((o) => ({
                          ...o,
                          party: d,
                          party_mail_detail: d.mail_detail,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded p-3 md:p-8 my-4 md:my-10 h-auto overflow-auto w-full">
          <div className="mt-3 w-full">
            <div className="w-full">
              <div className="w-full">
                <div className="flex gap-2 items-center justify-between">
                  <h3 className="text-sm font-bold">Items</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pb-20">
                      {data.bom_items.map((comp, i) => {
                        return (
                          <BOMItemComponent
                            data={comp}
                            edit={false}
                            disabled={true}
                            key={comp.id}
                            onDelete={(d: any) => {
                              setData((o) => ({
                                ...o,
                                bom_items: [...(o.bom_items || [])].filter(
                                  (i) => i.id !== d.id,
                                ),
                              }));
                            }}
                            onSubmit={(data: BOMItemModel): void => {
                              setData((o: NPDModel) => ({
                                ...o,
                                bom_items: [
                                  ...(o.bom_items || []).map((it) => {
                                    if (it.id === data.id) return data;
                                    return it;
                                  }),
                                ],
                              }));
                            }}
                            existingItems={existingItems}
                          />
                        );
                      })}

                      {!data.id && (
                        <BOMItemComponent
                          key={addNewId1}
                          onSubmit={function (data: BOMItemModel): void {
                            setData((o: NPDModel) => ({
                              ...o,
                              bom_items: [...(o.bom_items || []), data],
                            }));
                            setAddNewId1(Math.random() * 1000);
                          }}
                          // index={i}
                          edit={true}
                          disabled={false}
                          existingItems={existingItems}
                        />
                      )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {data.status !== NPDStatus.submitted && (
          <div>
            <div className="flex gap-2 items-center">
              <input
                id="checked-checkbox"
                type="checkbox"
                checked={checked} // Make sure to control the input
                onChange={(e) => setChecked(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="checked-checkbox"
                className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
              >
                I have reviewed and finalized the BOM for production.
              </label>
            </div>

            <div className="flex flex-wrap sticky bottom-0 justify-end my-3 md:my-5 gap-2 md:gap-4 px-2 md:px-5 py-3 md:py-5 bg-white">
              <button
                type="button"
                className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
                onClick={() => setShowFinalReviewModel(true)}
                // disabled={isWithdrawn} // Disable submit if withdrawn or no items
              >
                Final Submit
              </button>
              <button
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
                onClick={() => navigate.push(urls.npd)}
              >
                Quit
              </button>
            </div>
          </div>
        )}
        {data.status === NPDStatus.submitted && (
          <div>
            <div className="my-5">
              <div className="flex gap-2 items-center justify-between">
                <h3 className="text-sm font-bold">Returned Stocks</h3>

                <div className="">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleReturnedStockCSVUpload}
                    className="hidden"
                  />

                  {/* <label id="csvUpload"> */}
                  <label
                    onClick={handleDownloadTemplate}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                  >
                    Template
                  </label>
                  <button
                    onClick={handleButtonClick}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
                  >
                    Upload Returned Stocks
                  </button>
                  {/* </label> */}
                </div>
                {/* <button onClick={()=>{}} className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1">Upload</button> */}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pb-4">
                    {data?.returned_items?.map((comp, i) => {
                      return (
                        <BOMReturnedItemComponent
                          data={comp}
                          edit={false}
                          disabled={false}
                          key={comp.id}
                          onDelete={(d: any) => {
                            setData((o) => ({
                              ...o,
                              returned_items: [
                                ...(o.returned_items || []),
                              ].filter((i) => i.id !== d.id),
                            }));
                          }}
                          onSubmit={(data: BOMItemModel): void => {
                            setData((o: NPDModel) => ({
                              ...o,
                              returned_items: [
                                ...(o.returned_items || []).map((it) => {
                                  if (it.id === data.id) return data;
                                  return it;
                                }),
                              ],
                            }));
                          }}
                          existingItems={existingReturnedItems}
                        />
                      );
                    })}
                    {data.status === NPDStatus.submitted && (
                      <BOMReturnedItemComponent
                        key={addNewId1}
                        onSubmit={function (data: BOMItemModel): void {
                          setData((o: NPDModel) => ({
                            ...o,
                            returned_items: [...(o.returned_items || []), data],
                          }));
                          setAddNewId1(Math.random() * 1000);
                        }}
                        edit={true}
                        disabled={false}
                        existingItems={existingReturnedItems}
                      />
                    )}
                </div>
                <div className="flex flex-wrap sticky bottom-0 justify-end my-3 md:my-5 gap-2 md:gap-4 px-2 md:px-5 py-3 md:py-5 bg-white">
                  <button
                    type="button"
                    className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
                    onClick={() => {
                      ReturnedSubmit();
                    }}
                  >
                    Returned Submit
                  </button>
                </div>
            </div>
            <div className="flex flex-wrap sticky justify-end my-3 md:my-5 gap-2 md:gap-4 px-2 md:px-5 py-3 md:py-5 bg-white">
              {/* <button
                type="button"
                className="px-10 py-1 rounded-md bg-myPrimaryColor text-white text-sm"
                onClick={() => {
                  ReturnedSubmit();
                }}
              >
                Returned Submit
              </button> */}
              <button
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
                onClick={() => navigate.push(urls.npd)}
              >
                Quit
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NPDFinalViewScreen;
